import { useEffect, useRef, useState } from 'react';
import { settleable, whenSettled } from './motion/animations';

/**
 * WAS A SKELETON JUST REPLACED BY THE REAL THING?
 *
 * A placeholder and the content that replaces it are two different pictures in one
 * rectangle, swapped in a single frame, with nothing connecting them - which is what
 * makes a loading screen end in a flicker rather than in an arrival. `.content-blend`
 * (ui/motion.css) is the connection: opacity only, so nothing moves and no layout
 * property is touched on the frame the data lands.
 *
 * THE POINT OF THE HOOK IS THE OTHER HALF: content that never waited must not fade.
 * Most navigations in this product hit the TanStack cache and render instantly, and a
 * fade over data that was already there is a delay somebody paid for nothing.
 * So the blend is armed by the loading -> loaded EDGE and by nothing else; a component
 * that mounts with its data already resolved never returns `true`.
 *
 * It disarms itself after the animation rather than staying on, so a remount later in
 * the session does not silently replay it.
 */

/**
 * WHAT DISARMS IT IS THE ANIMATION, NOT A CLOCK. This used to be `BLEND_MS = 240`, a hand
 * copy of a duration token that had drifted: the blend runs for `--duration-system`, which
 * is 250ms, so the class came off ten milliseconds before the fade it was carrying had
 * finished and the last frames of every blend in the product were dropped. No number in
 * this file mirrors a token any more - the hook reads the animation the browser is actually
 * running and waits for it to settle, the same way `useMotion` does (`motion/animations.ts`).
 *
 * IT FINDS THAT ANIMATION BY THE CLASS RATHER THAN BY A REF, because the hook's caller hands
 * it a boolean and gets a boolean back: the element that carries `.content-blend` belongs to
 * the caller, and asking four screens for a ref would be a change to four screens for a value
 * this hook can read off the document. Two blends running at once wait for each other, which
 * costs nothing - they are the same animation, started on the same frame, for the same length.
 */
function blendAnimations(): readonly Animation[] {
  if (typeof document === 'undefined' || typeof document.getAnimations !== 'function') return [];
  return settleable(
    document.getAnimations().filter((animation) => {
      const target = (animation.effect as KeyframeEffect | null)?.target;
      return target instanceof Element && target.classList.contains('content-blend');
    }),
  );
}

export function useLoadingBlend(loading: boolean): boolean {
  const [blending, setBlending] = useState(false);
  const wasLoading = useRef(loading);

  useEffect(() => {
    const armed = wasLoading.current && !loading;
    wasLoading.current = loading;
    if (!armed) return undefined;

    setBlending(true);
    return undefined;
  }, [loading]);

  useEffect(() => {
    if (!blending) return undefined;
    let live = true;
    const stop = (): void => {
      if (!live) return;
      live = false;
      setBlending(false);
    };

    /**
     * ONE FRAME BEFORE LOOKING. This effect runs on the commit that turned the class on,
     * and the animation the class starts is created on the style recalculation after it;
     * asking for the list here would find nothing every time. Where there is still nothing
     * a frame later - a unit environment, a browser that ran no animation - the blend is
     * over as far as this hook is concerned, which is the same next-frame answer `useMotion`
     * gives to the same question.
     */
    const frame = requestAnimationFrame(() => {
      const running = blendAnimations();
      if (running.length === 0) {
        stop();
        return;
      }
      void whenSettled(running).then(stop);
    });

    return () => {
      live = false;
      cancelAnimationFrame(frame);
    };
  }, [blending]);

  return blending;
}
